import { useState, useEffect } from 'react';
import './BookingModal.css';

const TICKET_TYPES = ['day', 'vip', 'student', 'family'];
const PAYMENT_METHODS = ['card', 'wallet', 'cash'];

export default function BookingModal({ booking, onClose, onSave }) {
  const isEdit = !!booking?.id;

  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    date: '',
    ticket_type: 'day',
    quantity: 1,
    payment_method: 'card',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (booking) {
      setForm({
        full_name: booking.full_name || '',
        email: booking.email || '',
        phone: booking.phone || '',
        date: booking.date || '',
        ticket_type: booking.ticket_type || 'day',
        quantity: booking.quantity || 1,
        payment_method: booking.payment_method || 'card',
      });
    }
  }, [booking]);

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = 'Name is required';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Invalid email';
    if (!form.date) e.date = 'Date is required';
    if (!form.quantity || form.quantity < 1) e.quantity = 'At least 1 ticket';
    return e;
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setLoading(true);
    await onSave(form);
    setLoading(false);
  };

  return (
    <div className="bm-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bm-panel">
        <div className="bm-header">
          <div className="bm-title-block">
            <span className="bm-tag">{isEdit ? 'Edit' : 'New'}</span>
            <h2 className="bm-title">{isEdit ? 'Edit Booking' : 'New Booking'}</h2>
          </div>
          <button className="bm-close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div className="bm-body">
          <div className="bm-section-label">Guest Info</div>

          <div className="bm-row">
            <div className={`bm-field ${errors.full_name ? 'bm-field--error' : ''}`}>
              <label>Full Name</label>
              <input
                type="text"
                value={form.full_name}
                onChange={e => handleChange('full_name', e.target.value)}
                placeholder="e.g. Jana Mahmoud"
              />
              {errors.full_name && <span className="bm-error">{errors.full_name}</span>}
            </div>
          </div>

          <div className="bm-row bm-row--2">
            <div className={`bm-field ${errors.email ? 'bm-field--error' : ''}`}>
              <label>Email</label>
              <input
                type="email"
                value={form.email}
                onChange={e => handleChange('email', e.target.value)}
                placeholder="email@example.com"
              />
              {errors.email && <span className="bm-error">{errors.email}</span>}
            </div>
            <div className="bm-field">
              <label>Phone <span className="bm-optional">(optional)</span></label>
              <input
                type="tel"
                value={form.phone}
                onChange={e => handleChange('phone', e.target.value)}
                placeholder="+20 xxx xxx xxxx"
              />
            </div>
          </div>

          <div className="bm-divider" />
          <div className="bm-section-label">Ticket Details</div>

          <div className="bm-row bm-row--2">
            <div className={`bm-field ${errors.date ? 'bm-field--error' : ''}`}>
              <label>Date</label>
              <input
                type="date"
                value={form.date}
                onChange={e => handleChange('date', e.target.value)}
              />
              {errors.date && <span className="bm-error">{errors.date}</span>}
            </div>
            <div className={`bm-field ${errors.quantity ? 'bm-field--error' : ''}`}>
              <label>Quantity</label>
              <div className="bm-qty">
                <button
                  className="bm-qty-btn"
                  onClick={() => handleChange('quantity', Math.max(1, form.quantity - 1))}
                >−</button>
                <input
                  type="number"
                  min="1"
                  value={form.quantity}
                  onChange={e => handleChange('quantity', parseInt(e.target.value) || 1)}
                />
                <button
                  className="bm-qty-btn"
                  onClick={() => handleChange('quantity', form.quantity + 1)}
                >+</button>
              </div>
              {errors.quantity && <span className="bm-error">{errors.quantity}</span>}
            </div>
          </div>

          <div className="bm-row bm-row--2">
            <div className="bm-field">
              <label>Ticket Type</label>
              <div className="bm-chips">
                {TICKET_TYPES.map(t => (
                  <button
                    key={t}
                    className={`bm-chip ${form.ticket_type === t ? 'bm-chip--active' : ''}`}
                    onClick={() => handleChange('ticket_type', t)}
                  >
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div className="bm-field">
              <label>Payment Method</label>
              <div className="bm-chips">
                {PAYMENT_METHODS.map(p => (
                  <button
                    key={p}
                    className={`bm-chip ${form.payment_method === p ? 'bm-chip--active' : ''}`}
                    onClick={() => handleChange('payment_method', p)}
                  >
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="bm-footer">
          <button className="bm-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="bm-btn-save" onClick={handleSubmit} disabled={loading}>
            {loading ? (
              <span className="bm-spinner" />
            ) : (
              isEdit ? 'Save Changes' : 'Create Booking'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}